import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import type { MeasurementPoint } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo, formatDateTime, formatNumber } from "../format.ts";
import { pathTo, scope, setScope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { freshness } from "../overview.ts";
import { pointSeries, type PointSeries } from "../pointData.ts";
import {
  alarmedAt,
  groupMarks,
  placeLabels,
  type MarkGroup,
  HISTORY_HOURS,
  hourMean,
  hourStart,
  northAngle,
  planArea,
  scaleBar,
} from "../plans.ts";
import { Icon } from "../icons.tsx";
import { mediumColor } from "../components/Media.tsx";
import { unitLabel } from "../components/PointCard.tsx";
import { SitesMap } from "../components/SitesMap.tsx";
import { PlaceMap } from "../components/PlaceMap.tsx";
import { PlanWizard } from "../components/PlanWizard.tsx";
import { type MapMark, PlanMapView } from "../components/PlanMap.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { NodeDialog } from "./AssetsPage.tsx";
import {
  ConfirmDialog,
  operator,
  PageHeader,
  Skeleton,
} from "../components/ui.tsx";

type Tab = "sites" | "site" | "plan";
type Layer = "power" | "temp" | "events";

/**
 * Floor plans and maps (prototype "Rzuty i mapy"): the organisation's places on a map, the scope place with its
 * buildings on a map, and the floor plan of the scope with its points on it — their values now or at an hour of the
 * last week, and the events that were on. Marks point at points of the tree; they are not copies of devices.
 */
export function PlansPage() {
  const [tab, setTab] = createSignal<Tab>("plan");
  const [addingPlace, setAddingPlace] = createSignal(false);
  const path = () =>
    pathTo(siteTree()?.nodes ?? [], scope()?.id)
      .map((n) => n.name)
      .join(" / ");
  const root = () =>
    siteTree()?.nodes.find((n) => n.parentId === null)?.name ?? "";
  return (
    <>
      <PageHeader
        eyebrow={`${t("plans.eyebrow")} · ${tab() === "sites" ? root() : path()}`}
        title={tab() === "plan" ? t("plans.title") : t(`plans.tabs.${tab()}`)}
        sub={t(`plans.intro.${tab()}`)}
      >
        <div class="seg" role="group" aria-label={t("plans.viewLabel")}>
          <For each={["sites", "site", "plan"] as const}>
            {(v) => (
              <button
                type="button"
                aria-pressed={tab() === v}
                onClick={() => setTab(v)}
              >
                <Icon name={v === "plan" ? "floor" : "map"} />{" "}
                {v === "plan"
                  ? t("plans.tabs.planOf", { name: scope()?.name ?? "" })
                  : t(`plans.tabs.${v}`)}
              </button>
            )}
          </For>
        </div>
        <Show when={tab() === "site" && operator() && scope()}>
          <button
            type="button"
            class="btn"
            onClick={() => setAddingPlace(true)}
          >
            <Icon name="plus" />
            {t("plans.site.add")}
          </button>
        </Show>
      </PageHeader>
      <Show when={tab() === "sites"}>
        <Show
          when={(siteTree()?.nodes ?? []).some((n) => n.latitude !== null)}
          fallback={
            <section class="card empty">
              <p class="muted">{t("plans.noMap")}</p>
            </section>
          }
        >
          <SitesMap />
        </Show>
      </Show>
      <Show when={tab() === "site"}>
        <Show when={scope()} keyed>
          {(node) => <PlaceMap nodeId={node.id} />}
        </Show>
      </Show>
      <Show when={tab() === "plan"}>
        <Show when={scope()} keyed>
          {(node) => <Plan nodeId={node.id} />}
        </Show>
      </Show>
      <Show when={addingPlace() && scope()}>
        <NodeDialog
          parentId={scope()!.id}
          onClose={() => setAddingPlace(false)}
          onSaved={(n) => setScope(n.id)}
        />
      </Show>
    </>
  );
}

/** A point's value on the plan: a number with its unit, and when it was read (null for an hour's mean). */
interface Shown {
  value: number | null;
  unit: string;
  at: string | null;
}

function Plan(props: { nodeId: string }) {
  const navigate = useNavigate();
  const [plan, { refetch }] = createResource(
    () => props.nodeId,
    (id) => api.plans.get(id),
  );
  const [image] = createResource(
    () =>
      plan() && !plan()!.map
        ? `${props.nodeId}|${plan()!.updatedAt}`
        : undefined,
    async () =>
      URL.createObjectURL(
        await api.plans.image(props.nodeId, plan()!.updatedAt),
      ),
  );
  createEffect(() => {
    const url = image();
    onCleanup(() => url && URL.revokeObjectURL(url));
  });
  const [size, setSize] = createSignal<{ w: number; h: number }>();
  createEffect(() => {
    const url = image();
    if (url) {
      const img = new Image();
      img.onload = () => setSize({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = url;
    }
  });
  const [readings] = createResource(
    () => props.nodeId,
    async (id) =>
      new Map((await api.sites.pointsNow(id)).map((r) => [r.pointId, r])),
  );
  const [events] = createResource(() => api.events.list());
  const [layer, setLayer] = createSignal<Layer>("power");
  const [hour, setHour] = createSignal(HISTORY_HOURS);
  const live = () => hour() === HISTORY_HOURS;
  const moment = () =>
    live() ? Date.now() : hourStart(Date.now(), HISTORY_HOURS - hour());
  const [wizard, setWizard] = createSignal(false);
  const [removing, setRemoving] = createSignal(false);

  const points = createMemo(() => {
    const inside = new Set(subtreeOf(siteTree()?.nodes ?? [], props.nodeId));
    return (siteTree()?.points ?? []).filter((p) => inside.has(p.nodeId));
  });
  const marks = () => plan()?.marks ?? [];
  const point = (id: string) => points().find((p) => p.id === id);
  const unplaced = () =>
    points().filter((p) => !marks().some((m) => m.pointId === p.id));
  const alarmed = createMemo(() => alarmedAt(events() ?? [], moment()));

  // The week of the marked points is read once, when the slider first leaves "now".
  const [wanted, setWanted] = createSignal(false);
  createEffect(() => !live() && setWanted(true));
  const [history] = createResource(
    () => (wanted() && plan() && readings() ? plan()!.marks : undefined),
    async (ms) => {
      const to = new Date();
      const from = new Date(hourStart(to.getTime(), HISTORY_HOURS));
      const all = await Promise.all(
        ms
          .map((m) => point(m.pointId))
          .filter((p): p is MeasurementPoint => p !== undefined)
          .map((p) =>
            pointSeries(p, readings()?.get(p.id), from, to).catch(() => null),
          ),
      );
      return new Map(
        all
          .filter((s): s is PointSeries => s !== null)
          .map((s) => [s.point.id, s]),
      );
    },
  );

  const shown = (id: string): Shown => {
    if (live()) {
      const r = readings()?.get(id);
      return {
        value: r?.value ?? null,
        unit: r?.unit ?? "",
        at: r?.at ?? null,
      };
    }
    const s = history()?.get(id);
    return {
      value: s ? hourMean(s.values, moment()) : null,
      unit: s?.unit ?? "",
      at: null,
    };
  };
  const kw = (v: Shown) =>
    v.value === null
      ? null
      : v.unit === "kW"
        ? v.value
        : v.unit === "W"
          ? v.value / 1000
          : null;
  const isTemp = (v: Shown) => v.unit === "degC";
  const label = (v: Shown) =>
    v.value === null
      ? "—"
      : `${formatNumber(v.value, locale(), Math.abs(v.value) < 10 ? 2 : 1)} ${unitLabel(v.unit)}`;

  const W = () => size()?.w ?? 1000;
  const H = () => size()?.h ?? 700;
  const s = () => W() / 1000;
  const bar = () => {
    const w = plan()?.widthM;
    return w ? scaleBar(w) : null;
  };
  const north = () => northAngle(plan()?.orientation ?? "none");
  const open = (id: string) =>
    navigate(`/assets?point=${encodeURIComponent(id)}`);

  // One marker per device at one spot (phases and their sum together), one per point otherwise.
  const groups = createMemo(() => groupMarks(marks(), points()));
  const pointView = (id: string) => {
    const pt = point(id);
    const v = shown(id);
    const state = !pt?.binding
      ? "none"
      : live()
        ? freshness(v.at, Date.now())
        : v.value === null
          ? "none"
          : "ok";
    return {
      pt,
      v,
      state,
      alarm: alarmed().has(id),
      text:
        state === "late"
          ? `${label(v)} · ${formatAgo(v.at, locale())}`
          : label(v),
    };
  };
  const groupView = (g: MarkGroup) => {
    const rows = g.pointIds.map((id, i) => ({
      name: g.names[i] ?? "",
      ...pointView(id),
    }));
    const ok = rows.some((r) => r.state === "ok");
    const alarm = rows.some((r) => r.alarm);
    const temp = rows.some((r) => isTemp(r.v));
    // The halo follows the largest power: a device's sum, not phases added to it again.
    const power = Math.max(
      0,
      ...rows.filter((r) => r.state === "ok").map((r) => kw(r.v) ?? 0),
    );
    return {
      rows,
      ok,
      late: !ok && rows.some((r) => r.state === "late"),
      alarm,
      temp,
      power,
      medium: rows[0]?.pt?.medium ?? "el",
      dim: (layer() === "temp" && !temp) || (layer() === "events" && !alarm),
    };
  };
  const rowText = (r: { name: string; text: string }) =>
    r.name ? `${r.name}: ${r.text}` : r.text;
  // Where each label of an image plan goes, so labels do not cover each other or other markers (plan units).
  const labelOffsets = createMemo(() =>
    placeLabels(
      groups().map((g) => {
        const lines = [g.title, ...groupView(g).rows.map(rowText)];
        return {
          x: g.x * W(),
          y: g.y * H(),
          w: (Math.max(...lines.map((l) => l.length)) * 7.2 + 20) * s(),
          h: (22 + g.pointIds.length * 16) * s(),
        };
      }),
      8 * s(),
      4 * s(),
    ),
  );

  // The same markers on a map plan: x is longitude, y latitude; values, halos, rings and dimming as on an image.
  const mapMarks = (): MapMark[] =>
    groups().map((g) => {
      const view = groupView(g);
      return {
        pointId: g.pointIds[0]!,
        latitude: g.y,
        longitude: g.x,
        color: view.ok ? mediumColor(view.medium) : "var(--stale)",
        title: g.title,
        label: {
          rows: view.rows.map((r) => ({
            name: r.name,
            value: r.text,
            warn: r.state !== "ok",
          })),
          dashed: view.late,
        },
        halo:
          layer() === "power" && view.power > 0
            ? Math.sqrt(view.power) * 3.2
            : layer() === "temp" && view.temp
              ? 34
              : undefined,
        haloColor:
          layer() === "temp" ? "var(--m-temp)" : mediumColor(view.medium),
        ring: view.alarm,
        dim: view.dim,
      };
    });

  return (
    <Show when={!plan.error} fallback={<ErrorBox error={plan.error} />}>
      <Show
        when={plan.state === "ready" || plan()}
        fallback={
          <section class="card">
            <Skeleton lines={6} />
          </section>
        }
      >
        <Show
          when={plan()}
          fallback={
            <section class="card empty">
              <h2>{t("plans.noPlanTitle", { name: scope()?.name ?? "" })}</h2>
              <p class="muted">{t("plans.noPlan")}</p>
              <Show when={operator()}>
                <div>
                  <button
                    type="button"
                    class="btn primary"
                    onClick={() => setWizard(true)}
                  >
                    <Icon name="plus" />
                    {t("plans.add")}
                  </button>
                </div>
              </Show>
            </section>
          }
        >
          {(p) => (
            <section class="card plan">
              <div class="note" style={{ "flex-wrap": "wrap", gap: "10px" }}>
                <div class="seg" role="group" aria-label={t("plans.layer")}>
                  <For each={["power", "temp", "events"] as const}>
                    {(l) => (
                      <button
                        type="button"
                        aria-pressed={layer() === l}
                        onClick={() => setLayer(l)}
                      >
                        {t(`plans.layers.${l}`)}
                      </button>
                    )}
                  </For>
                </div>
                <span class="hint">
                  {[
                    t("plans.updated", {
                      by: p().updatedBy,
                      at: formatDateTime(p().updatedAt, locale()),
                    }),
                    p().map
                      ? t(`plans.map.basemap.${p().map!.basemap}`)
                      : p().widthM
                        ? t("plans.scaleInfo", {
                            width: formatNumber(p().widthM!, locale(), 0),
                            area: formatNumber(
                              planArea(p().widthM!, W(), H()),
                              locale(),
                              0,
                            ),
                          })
                        : t("plans.noScale"),
                    t("plans.markCount", { count: p().marks.length }),
                  ].join(" · ")}
                </span>
                <Show when={operator()}>
                  <span
                    style={{
                      "margin-left": "auto",
                      display: "flex",
                      gap: "6px",
                      "flex-wrap": "wrap",
                    }}
                  >
                    <button
                      type="button"
                      class="btn sm"
                      onClick={() => setWizard(true)}
                    >
                      <Icon name="edit" />
                      {t("plans.edit")}
                    </button>
                    <button
                      type="button"
                      class="btn sm danger"
                      onClick={() => setRemoving(true)}
                    >
                      {t("common.delete")}
                    </button>
                  </span>
                </Show>
              </div>
              <Show when={p().map} keyed>
                {(m) => (
                  <PlanMapView
                    basemap={m.basemap}
                    view={m}
                    marks={mapMarks()}
                    label={t("plans.title")}
                    height="max(460px, calc(100vh - 300px))"
                    onMarkClick={open}
                    fit
                  />
                )}
              </Show>
              <div
                class="scrollx"
                style={{ display: p().map ? "none" : undefined }}
              >
                <Show
                  when={image()}
                  fallback={
                    <div class="card-b">
                      <Skeleton lines={8} />
                    </div>
                  }
                >
                  <svg
                    viewBox={`0 0 ${W()} ${H()}`}
                    role="img"
                    aria-label={t("plans.title")}
                    style={{
                      width: "100%",
                      "min-width": "600px",
                      display: "block",
                    }}
                  >
                    <image
                      href={image()}
                      x="0"
                      y="0"
                      width={W()}
                      height={H()}
                    />
                    <For each={groups()}>
                      {(g, gi) => {
                        const off = () =>
                          labelOffsets()[gi()] ?? {
                            dx: 12 * s(),
                            dy: -28 * s(),
                          };
                        const view = () => groupView(g);
                        const x = () => g.x * W();
                        const y = () => g.y * H();
                        const lines = () => [
                          g.title,
                          ...view().rows.map(rowText),
                        ];
                        const width = () =>
                          (Math.max(...lines().map((l) => l.length)) * 7.2 +
                            20) *
                          s();
                        const height = () =>
                          (22 + view().rows.length * 16) * s();
                        return (
                          <g
                            class="mkr"
                            role="button"
                            tabindex="0"
                            aria-label={lines().join(", ")}
                            style={{ cursor: "pointer" }}
                            opacity={view().dim ? 0.35 : 1}
                            onClick={() => open(g.pointIds[0]!)}
                            onKeyDown={(e) =>
                              e.key === "Enter" && open(g.pointIds[0]!)
                            }
                          >
                            <Show
                              when={layer() === "power" && view().power > 0}
                            >
                              <circle
                                cx={x()}
                                cy={y()}
                                r={Math.sqrt(view().power) * 3.2 * s()}
                                fill={mediumColor(view().medium)}
                                opacity="0.18"
                              />
                            </Show>
                            <Show when={layer() === "temp" && view().temp}>
                              <circle
                                cx={x()}
                                cy={y()}
                                r={40 * s()}
                                fill="var(--m-temp)"
                                opacity="0.14"
                              />
                            </Show>
                            <Show when={view().alarm}>
                              <circle
                                cx={x()}
                                cy={y()}
                                r={16 * s()}
                                fill="none"
                                stroke="var(--crit)"
                                stroke-width={2.5 * s()}
                              />
                            </Show>
                            <circle
                              cx={x()}
                              cy={y()}
                              r={8 * s()}
                              fill={
                                view().ok
                                  ? mediumColor(view().medium)
                                  : "var(--stale)"
                              }
                              stroke="var(--surface)"
                              stroke-width={2 * s()}
                            />
                            <rect
                              x={x() + off().dx}
                              y={y() + off().dy}
                              width={width()}
                              height={height()}
                              rx={5 * s()}
                              fill="var(--surface)"
                              stroke={
                                view().alarm ? "var(--crit)" : "var(--line-2)"
                              }
                              stroke-dasharray={
                                view().late
                                  ? `${3 * s()} ${3 * s()}`
                                  : undefined
                              }
                              opacity="0.94"
                            />
                            <text
                              x={x() + off().dx + 8 * s()}
                              y={y() + off().dy + 15 * s()}
                              style={{
                                "font-size": `${11 * s()}px`,
                                fill: "var(--muted)",
                                "font-family": "var(--font-ui)",
                              }}
                            >
                              {g.title}
                            </text>
                            <For each={view().rows}>
                              {(r, i) => (
                                <text
                                  x={x() + off().dx + 8 * s()}
                                  y={y() + off().dy + (31 + i() * 16) * s()}
                                  style={{
                                    "font-size": `${12 * s()}px`,
                                    fill:
                                      r.state === "ok"
                                        ? "var(--ink)"
                                        : "var(--warn)",
                                    "font-family": "var(--font-mono)",
                                  }}
                                >
                                  {rowText(r)}
                                </text>
                              )}
                            </For>
                          </g>
                        );
                      }}
                    </For>
                    <Show when={bar()}>
                      {(b) => {
                        const len = () => b().fraction * W();
                        const x0 = () => 24 * s();
                        const y0 = () => H() - 24 * s();
                        return (
                          <g aria-hidden="true">
                            <rect
                              x={x0() - 8 * s()}
                              y={y0() - 26 * s()}
                              width={len() + 16 * s()}
                              height={36 * s()}
                              rx={4 * s()}
                              fill="var(--surface)"
                              opacity="0.85"
                            />
                            <path
                              d={`M${x0()} ${y0() - 6 * s()}V${y0()}H${x0() + len()}V${y0() - 6 * s()}`}
                              fill="none"
                              stroke="var(--ink)"
                              stroke-width={2 * s()}
                            />
                            <text
                              x={x0()}
                              y={y0() - 10 * s()}
                              style={{
                                "font-size": `${11 * s()}px`,
                                fill: "var(--ink)",
                                "font-family": "var(--font-mono)",
                              }}
                            >
                              {b().metres} m
                            </text>
                          </g>
                        );
                      }}
                    </Show>
                    <Show when={north() !== null}>
                      <g
                        aria-hidden="true"
                        transform={`rotate(${north()} ${W() - 34 * s()} ${38 * s()})`}
                      >
                        <circle
                          cx={W() - 34 * s()}
                          cy={38 * s()}
                          r={22 * s()}
                          fill="var(--surface)"
                          opacity="0.85"
                        />
                        <path
                          d={`M${W() - 34 * s()} ${24 * s()}L${W() - 27 * s()} ${46 * s()}L${W() - 34 * s()} ${41 * s()}L${W() - 41 * s()} ${46 * s()}Z`}
                          fill="var(--ink)"
                        />
                        <text
                          x={W() - 34 * s()}
                          y={21 * s()}
                          text-anchor="middle"
                          style={{
                            "font-size": `${10 * s()}px`,
                            fill: "var(--ink)",
                            "font-family": "var(--font-ui)",
                          }}
                        >
                          N
                        </text>
                      </g>
                    </Show>
                  </svg>
                </Show>
              </div>
              <div class="slider">
                <span class="eyebrow">{t("plans.time")}</span>
                <input
                  type="range"
                  min="0"
                  max={HISTORY_HOURS}
                  step="1"
                  value={hour()}
                  aria-label={t("plans.timeLabel")}
                  onInput={(e) => setHour(Number(e.currentTarget.value))}
                />
                <span class="num" style={{ "min-width": "150px" }}>
                  {live()
                    ? t("plans.now")
                    : formatDateTime(
                        new Date(moment()).toISOString(),
                        locale(),
                      )}
                </span>
                <Show
                  when={live()}
                  fallback={
                    <span class="chip">
                      {history.loading
                        ? t("plans.historyLoading")
                        : t("plans.history")}
                    </span>
                  }
                >
                  <span class="chip ok">{t("plans.live")}</span>
                </Show>
              </div>
              <div class="legend" style={{ "padding-top": "10px" }}>
                <span>{t(`plans.legend.${layer()}`)}</span>
                <Show when={unplaced().length > 0}>
                  <span class="hint">
                    {t("plans.unplaced", { count: unplaced().length })}
                  </span>
                </Show>
              </div>
              <Show when={removing()}>
                <ConfirmDialog
                  title={t("plans.removeTitle")}
                  text={t("plans.removeText")}
                  confirm={t("common.delete")}
                  danger
                  onConfirm={async () => {
                    await api.plans.remove(props.nodeId);
                    await refetch();
                  }}
                  onClose={() => setRemoving(false)}
                />
              </Show>
            </section>
          )}
        </Show>
        <p class="hint" style={{ margin: 0 }}>
          {t("plans.footHint")}
        </p>
        <Show when={wizard()}>
          <PlanWizard
            nodeId={props.nodeId}
            plan={plan() ?? null}
            image={image()}
            onClose={() => setWizard(false)}
            onSaved={() => void refetch()}
          />
        </Show>
      </Show>
    </Show>
  );
}
